import { createSafeUserPreferences } from '@wix/wix-data-client-common-standalone'
import { getBiDefaults } from '@wix/wix-data-client-common/dist/esm/bi/getBiDefaults'
import { getAppVersion } from '@wix/wix-data-client-common/dist/esm/infra/getAppVersion'
import { isEditor3 } from '@wix/wix-data-client-common'
import { cmCreateCollectionClicked } from '@wix/bi-logger-platform-cm/v2'

import openContentManager from './openContentManager'
import {
  shouldShowIntroPanel,
  openIntroPanel,
  MAIN_ACTION_CLICKED,
} from './introPanelForVelo'
import {
  getCMSWebCreateCollectionPanelUrl,
  getCMSWebCreateCollectionPanelV2Url,
  getCMSWebEditCollectionPanelV2Url,
} from './cms-web-panels'

const reportCreateCollectionClicked = async ({
  origin,
  logger,
  editorSdkProxy,
}) => {
  try {
    const appVersion = await getAppVersion(editorSdkProxy)
    const biDefaults = await getBiDefaults({
      editorSdkProxy,
      appVersion,
    })

    logger.bi(
      cmCreateCollectionClicked({
        ...biDefaults,
        origin,
      }),
    )
  } catch (error) {
    logger.error(
      new Error('Failed to report CreateCollectionClicked action', {
        cause: error,
      }),
    )
  }
}

export default ({
  hostname,
  collectionsApi,
  fedopsLogger,
  logger,
  editorSdkProxy,
  initialScreen,
  wixData,
  i18n,
  appApi,
  editorType,
  experimentsManager,
}) => {
  const safeUserPreferences = createSafeUserPreferences({
    editorSdkProxy,
    logger,
    fedopsLogger,
  })

  return async initialData => {
    appApi.disallowCollectionSchemaRefresh()
    try {
      const { origin } = initialData || {}
      const editMode = Boolean(initialData.collectionId)
      const minimalConfiguration = isEditor3(editorType)
      const inAppBuilder = Boolean(editorSdkProxy.appBuilder)

      if (!editMode) {
        reportCreateCollectionClicked({
          origin,
          logger,
          editorSdkProxy,
        })
      }

      if (
        await shouldShowIntroPanel({
          editMode,
          inAppBuilder,
          safeUserPreferences,
          initialScreen,
        })
      ) {
        const result = await openIntroPanel({
          editorSdkProxy,
          safeUserPreferences,
          t: i18n.t.bind(i18n),
        })

        if (result !== MAIN_ACTION_CLICKED) {
          return
        }
      }

      const dimensions = getPanelDimensions({
        editMode,
        minimalConfiguration,
        experimentsManager,
      })

      const url = await getCollectionPanelUrl({
        hostname,
        editorSdkProxy,
        editMode,
        experimentsManager,
      })

      const panelResult = await editorSdkProxy.editor
        .openModalPanel({
          url,
          ...dimensions,
          shouldHideHeader: true,
          initialData: {
            initialScreen,
            ...initialData,
          },
        })
        .catch(error => {
          logger.error(
            new Error('Failed to open create/edit collection panel', {
              cause: error,
            }),
          )
        })

      if (panelResult && !editMode) {
        openContentManager({
          hostname,
          editorSdkProxy,
          collectionName: panelResult.collectionId,
          origin: initialData.origin,
          collectionsApi,
          wixData,
          i18n,
          logger,
          appApi,
          importRequested: panelResult.importRequested,
        }).catch(error => {
          logger.error(
            new Error(
              'Failed to open content manager after collection creation',
              {
                cause: error,
              },
            ),
          )
        })
      }

      return panelResult
    } finally {
      appApi.allowCollectionSchemaRefresh()
    }
  }
}

function getCollectionPanelUrl({
  hostname,
  editorSdkProxy,
  editMode,
  experimentsManager,
}) {
  if (editMode) {
    return getCMSWebEditCollectionPanelV2Url({ hostname, editorSdkProxy })
  }

  return experimentsManager.enabled(
    'specs.wixCode.CMSWebCreateCollectionPanelV2',
  )
    ? getCMSWebCreateCollectionPanelV2Url({ hostname, editorSdkProxy })
    : getCMSWebCreateCollectionPanelUrl({ hostname, editorSdkProxy })
}

function getPanelDimensions({
  editMode,
  minimalConfiguration,
  experimentsManager,
}) {
  if (editMode && minimalConfiguration) {
    return {
      width: 530,
      height: 390,
    }
  }

  if (editMode) {
    return {
      width: 798,
      height: 696,
    }
  }

  if (
    experimentsManager.enabled('specs.wixCode.CMSWebCreateCollectionPanelV2')
  ) {
    return {
      width: 742,
      height: 508,
    }
  }

  return {
    width: 744,
    height: 462,
  }
}
